import * as React from "react";
import * as S from "@styled";
import { flexRender, getCoreRowModel, useReactTable, getPaginationRowModel } from '@tanstack/react-table'
import { DraftType, IDraftListProps, IDropdownColumnVisibilityControlProps, ITableModeSelect, ITableProps } from "shared/model/Table";
import { Checkbox } from "shared/components/Checkbox";
import { DropdownButtonWithState } from "shared/components/DropdownButton";
import { TickWrapper } from "shared/components/TickWrapper";
import { PagninateProps } from "shared/styledComponents/Pagination";
import Select from "./select/Select";
import { Option } from "shared/model/Option";

// https://stackoverflow.com/a/53958176/13490198 Generic React component
const Table = <T extends object>(props: ITableProps<T> & React.PropsWithChildren<ITableProps<T>>) => {

    const table = useReactTable({
        data: props.data,
        columns: props.columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <S.StyledTable shouldWrapData={props.shouldWrapData} gridTemplateColumns={props.gridTemplateColumns} rowHeight={props.rowHeight} columnCount={props.columns.length}>
            <thead>
                {table.getHeaderGroups().map((headerGroup: any) => (
                    <tr key={headerGroup.id} className="header-row">
                        {headerGroup.headers.map((header: any) => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody>
                {table.getRowModel().rows.map((row: any) => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map((cell: any) => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </S.StyledTable>
    )
}

export const TableWithPagination = <T extends object>(props: ITableProps<T> & React.PropsWithChildren<ITableProps<T>>) => {

    const table = useReactTable({
        data: props.data,
        columns: props.columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    })

    const handlePageClick = (event: { selected: number }) => {
        table.setPageIndex(event.selected);
    };

    return (
        <>
            <S.StyledTable shouldWrapData={props.shouldWrapData} gridTemplateColumns={props.gridTemplateColumns} rowHeight={props.rowHeight} columnCount={props.columns.length}>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <th key={header.id} colSpan={header.colSpan}>
                                        {header.isPlaceholder ? null : (
                                            <div>
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </div>
                                        )}
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => {
                        return (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => {
                                    return (
                                        <td key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </S.StyledTable>
            {table.getPageCount() > 1 && (
                <S.FlexContainer style={{ marginTop: '24px'}}>
                    <TablePagination
                        forcePage={table.getState().pagination.pageIndex}
                        pageCount={table.getPageCount()}
                        onPageChange={handlePageClick}
                    />
                </S.FlexContainer>
            )}
        </>
    )
}

export const DraftListTable: React.FC<IDraftListProps> = (props) => {
    const visibleColumns: Array<keyof DraftType> = ["name", "modified", "modifier", "deleteAction"]
    const hiddenColumns: Array<keyof DraftType> = ["id", "processId"];

    const visibleColumnsMap = new Map(visibleColumns.map((vc) => [vc.valueOf(), true]));
    const hiddenColumnsMap = new Map(hiddenColumns.map((vc) => [vc.valueOf(), false]))

    const columnVisibility = new Map([...visibleColumnsMap, ...hiddenColumnsMap]);

    const table = useReactTable({
        data: props.data,
        columns: props.columns,
        state: {
            columnVisibility: Object.fromEntries(columnVisibility)
        },
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <S.StyledTable gridTemplateColumns={`1fr 1fr 1fr auto`} id="draft-list-table">
            <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="header-row">
                        {headerGroup.headers.map((header) => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody>
                {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </S.StyledTable>
    )
}

export const TableBottomControlsContainer: React.FC<React.PropsWithChildren> = ({ children }) => (
    <S.FlexContainer style={{ minHeight: "100px" }}>
        {children}
    </S.FlexContainer>
);

export const TableBottomControlItem: React.FC<React.PropsWithChildren & { justifyContent?: string }> = ({ children, justifyContent }) => (
    <S.FlexItem flex="0 0 33.33%" style={{ display: "flex", alignItems: "center", justifyContent: `${justifyContent || 'center'}`, }}>
        {children}
    </S.FlexItem>
);

export const TablePagination: React.FC<PagninateProps> = (props) => (
    <S.Pagination
        forcePage={props.forcePage}
        pageCount={props.pageCount}
        onPageChange={props.onPageChange}
        key={"pager"}
        breakLabel="..."
        nextLabel="Next"
        previousLabel="Previous"
        pageRangeDisplayed={4}
        marginPagesDisplayed={1}
        renderOnZeroPageCount={() => null}
        pageClassName="page-item"
        pageLinkClassName="page-link"
        previousClassName="page-item"
        previousLinkClassName="page-link"
        nextClassName="page-item"
        nextLinkClassName="page-link"
        breakClassName="page-item"
        breakLinkClassName="page-link"
        center={true}
    />
);

export const getTableModeSelectOptions = (options: number[], extras: Array<Option<number>>): Array<Option<number>> => {
    return options
        .map((row) => ({
            label: row.toString(),
            value: row
        }))
        .concat(extras);
}

export const TableModeSelect: React.FC<ITableModeSelect> = ({
    firstLabel,
    secondLabel,
    ...rest
}) => (
    <S.FlexContainer centerItems={true} justifyCenter={true} gap={15}>
        {firstLabel && <span>{firstLabel}</span>}

        <Select
            classNamePrefix="react-select"
            {...rest}
        />

        {secondLabel && <span>{secondLabel}</span>}
    </S.FlexContainer>
);

export const IndeterminateCheckbox = ({
    id, indeterminate, checked, onChange }:
    { id: string, indeterminate: boolean, checked: boolean, onChange: (event: unknown) => void }
) => {
    const ref = React.useRef<HTMLInputElement>(null!)

    React.useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !checked && indeterminate
        }
    }, [ref, indeterminate])

    return (
        <Checkbox
            ref={ref}
            className={"pointer"}
            checked={checked}
            onChange={onChange}
            id={id}
            style={{ margin: 0 }}
        />
    )
}

export function isColumnVisible<T>(columnId: T, visibleColumns?: T[], hiddenColumns?: T[]) {
    if (visibleColumns) {
        return visibleColumns.indexOf(columnId) >= 0;
    } else if (hiddenColumns) {
        return hiddenColumns.indexOf(columnId) === -1;
    }
    return true;
}

export const DropdownColumnVisibilityControl: React.FC<IDropdownColumnVisibilityControlProps<keyof any>> = (props) => {
    const handleButtonComponentClick = () => {
        if (props.open) {
            props.onHide();
        } else {
            props.onOpen();
        }
    };

    return (
        <DropdownButtonWithState
            open={props.open}
            onHide={props.onHide}
            id="column-visibility-dropdown"
            buttonComponent={(
                <S.Button type="button"
                    variant={S.ButtonType.text}
                    className="dropbtn"
                    title="Control column visibility"
                    onClick={handleButtonComponentClick}
                >
                    <i className="ci-slider_01 pointer" style={{ transform: "rotate(90deg)", fontSize: "24px" }} />
                </S.Button>
            )}
            dropDownButtonId="column-visibility-control-selector"
            dropDownButtonCustomStyles={{ justifyContent: "flex-end" }}
            dropDownContentPullRight={true}
            dropDownButtonTitle={`Adjust column visibility (${props.visibleColumns.length - 1} of ${props.maxVisibleColumns})`}
        >
            {props.possibleColumns.map((col) => {
                const isDisabled = props.visibleColumns.length - 1 === props.maxVisibleColumns && !props.visibleColumns.includes(col.column);
                return (
                    <button key={col.column} id={col.column} onClick={() => props.handleToggleColumn(col.column)} disabled={isDisabled}>
                        <S.FlexContainer centerItems={true} noWrap={true} whiteSpace={"nowrap"}>
                            {isColumnVisible(col.column, props.visibleColumns, props.hiddenColumns) ? (
                                <TickWrapper><i className="ci-check" /></TickWrapper>
                            ) : <TickWrapper />} <span>{col.label}</span>
                        </S.FlexContainer>
                    </button>
                )
            })}
        </DropdownButtonWithState>
    )
}

export default Table;
